/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react"
import { css, Dialog, DialogContent, DialogActions } from "@mui/material"
import { withMyTheme } from "../../../../theme/theme"
import { mobileCss } from "../../../../util/isMobile"
import { MyButton } from "../../../../components/button/MyButton"
import { MyInput } from "../../../../components/input/MyInput"
import { MyTimePicker } from "../../../../components/input/MyTimePicker"
import { useTranslation } from "react-i18next"
import { ScheduleEvent, MenuIconType } from "../../../common/context/SharedFormTypes"
import { ScheduleIconPicker } from "./ScheduleIconPicker"
import { parse, format } from "date-fns"

const DialogContentStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding: 2rem 2rem 1rem;

    ${mobileCss(`
        padding: 1.5rem 1rem 0.5rem;
    `)}
`)

const DialogActionsStyle = withMyTheme(() => css`
    padding: 1rem 2rem;
    justify-content: center;
    gap: 1rem;
`)

interface ScheduleEventDialogProps {
    open: boolean
    event: ScheduleEvent | null
    primaryColor: string
    onSave: (event: ScheduleEvent) => void
    onClose: () => void
}

export const ScheduleEventDialog = ({ open, event, primaryColor, onSave, onClose }: ScheduleEventDialogProps) => {
    const { t } = useTranslation()
    const [title, setTitle] = useState('')
    const [time, setTime] = useState('')
    const [iconType, setIconType] = useState<MenuIconType>('emoji')
    const [iconValue, setIconValue] = useState('💒')

    useEffect(() => {
        if (event) {
            setTitle(event.title)
            setTime(event.time || '')
            setIconType(event.iconType)
            setIconValue(event.iconValue)
        } else {
            setTitle('')
            setTime('')
            setIconType('emoji')
            setIconValue('💒')
        }
    }, [event, open])

    const handleIconSelect = (type: MenuIconType, value: string) => {
        setIconType(type)
        setIconValue(value)
    }

    const handleSave = () => {
        onSave({
            id: event?.id ?? `schedule-event-${Date.now()}`,
            title,
            time,
            iconType,
            iconValue
        })
        onClose()
    }

    const canSave = title.trim().length > 0

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
            <DialogContent css={DialogContentStyle}>
                <MyInput
                    value={title}
                    label={t('questionnaire.schedule.eventDialog.name')}
                    placeholder={t('questionnaire.schedule.eventDialog.namePlaceholder')}
                    onChange={setTitle}
                />

                <MyTimePicker
                    label={t('questionnaire.schedule.eventDialog.time')}
                    value={time ? parse(time, 'HH:mm', new Date()) : null}
                    onChange={(date) => setTime(date ? format(date, 'HH:mm') : '')}
                />

                <ScheduleIconPicker
                    iconType={iconType}
                    iconValue={iconValue}
                    primaryColor={primaryColor}
                    onSelect={handleIconSelect}
                />
            </DialogContent>

            <DialogActions css={DialogActionsStyle}>
                <MyButton
                    variant="secondary"
                    text={t('questionnaire.schedule.eventDialog.cancel')}
                    onClick={onClose}
                />
                <MyButton
                    variant="primary"
                    text={t('questionnaire.schedule.eventDialog.save')}
                    onClick={handleSave}
                    disabled={!canSave}
                />
            </DialogActions>
        </Dialog>
    )
}
